import { AtIcon, EditIcon, PhoneIcon, UserIcon } from "@xola/icons";
import { Phone } from "@xola/ui-kit";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Permission, Purchase, Traveler } from "types";
import { CopyableField } from "../../Common/CopyableField";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { EditedFormValues, EditPrimaryContactModal } from "./EditPrimaryContactModal";
import { MergeContactsModal } from "./MergeContactsModal";
import { MergedRecordBadge } from "./MergedRecordBadge";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";

export const ContactInformation = ({ contact, purchases }: { contact: Traveler; purchases: Purchase[] }) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const history = useHistory();

    const {
        isVisible: isVisibleEditContactModal,
        show: showEditContactModal,
        hide: hideEditContactModal,
    } = useModal(false);
    const [mergeContactsEditedValues, setMergeContactsEditedValues] = useState<EditedFormValues>();

    const handleMergeContactsSuccess = async (mergedContactId: string) => {
        setMergeContactsEditedValues(undefined);
        invalidate("sellers", seller.id);
        invalidate("users", mergedContactId);
        invalidate("users", contact.id);
        for (const purchase of purchases) {
            invalidate("purchases", purchase.id);
        }

        history.push(`/customers/contacts/${mergedContactId}`);
    };

    return (
        <div className="pt-10">
            <div className="align-center mb-5 flex justify-between">
                <div className="flex items-center space-x-2">
                    <h3>Primary Contact Information</h3>
                    <MergedRecordBadge contact={contact} />
                </div>
                <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                    <SecondaryButton
                        data-testid="contact-information-edit-contact-info-button"
                        size="small"
                        icon={<EditIcon />}
                        onClick={showEditContactModal}
                    >
                        Edit Contact Info
                    </SecondaryButton>
                </IsGranted>
            </div>

            <div className="mb-10 grid grid-cols-2 text-gray-darker">
                {contact.email ? (
                    <CopyableField
                        data={contact.email}
                        classNames={{ badge: "w-14" }}
                        dataTestId="contact-information-primary-email"
                        icon={<AtIcon className="mr-1" />}
                    >
                        {contact.email}
                    </CopyableField>
                ) : (
                    <span data-testid="contact-information-primary-email">
                        <AtIcon className="mr-1" />
                        No email provided
                    </span>
                )}
                {contact.phone && (
                    <CopyableField
                        data={contact.phone}
                        classNames={{ badge: "w-14" }}
                        dataTestId="contact-information-primary-phone"
                        icon={<PhoneIcon className="mr-1" />}
                    >
                        <Phone>{contact.phone}</Phone>
                    </CopyableField>
                )}
            </div>

            <EditPrimaryContactModal
                contact={contact}
                isOpen={isVisibleEditContactModal}
                onClose={hideEditContactModal}
                onDuplicate={(editedValues: EditedFormValues) => setMergeContactsEditedValues(editedValues)}
            />

            <MergeContactsModal
                contact={contact}
                editedValues={mergeContactsEditedValues}
                isOpen={!!mergeContactsEditedValues}
                onClose={() => setMergeContactsEditedValues(undefined)}
                onSuccess={handleMergeContactsSuccess}
            />

            <AdditionalContactInformation contact={contact} />
        </div>
    );
};

export const AdditionalContactInformation = ({ contact }: { contact: Traveler }) => {
    if (!contact.additionalNames?.length && !contact.additionalPhones?.length) {
        return null;
    }

    return (
        <>
            <h3 className="mb-5">Additional Contact Information</h3>
            <div className="grid grid-cols-2 text-gray-darker">
                <div className="flex-col space-y-2" data-testid="contact-information-additional-name">
                    {/* TODO X2-2213 : Update after additionalEmails or delete API available. */}
                    {contact.additionalNames?.map((name) => (
                        <NoTranslate key={name} as="div">
                            <UserIcon className="mr-1" />
                            {name}
                        </NoTranslate>
                    ))}
                </div>

                <div className="flex-col space-y-2" data-testid="contact-information-additional-phone">
                    {contact.additionalPhones?.map((phone) => (
                        <NoTranslate key={phone} as="div">
                            <PhoneIcon className="mr-1" />
                            <Phone>{phone}</Phone>
                        </NoTranslate>
                    ))}
                </div>
            </div>
        </>
    );
};
